import { MsgPayLoad, WebsocketService } from './../../service/websocket.service';
import { SolutionInfo } from './../../model/SolutionInfo';
import { SolutionService } from './../../service/solution.service';
import { Component, OnInit, Input } from '@angular/core';
import { AuthenticationService } from '../../service/authentication.service';
import { EventService } from '../../service/event.service';
import { Subscription } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { Router, NavigationEnd, NavigationCancel } from '@angular/router';
import { Events } from '../../service/websocket.service';
import { LoginState } from 'src/app/model/LoginState';
import { UserInfo } from 'src/app/model/User';
import { Utils } from 'src/app/utils';
import { MessageBannerLink } from '../controls/message-banner/message-banner.component';
import { GeneralService } from 'src/app/service/general.service';
import { GlobalMenu } from 'src/app/model/GlobalMenu';
import { Tab, TabColor } from '../controls/tabs/tabs.component';

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.css'],
    standalone: false
})
export class MainComponent implements OnInit {
  @Input() isForceGetSolutionInfo: boolean = false;
  loginEventSubscription: Subscription;
  routerEventSubscription: Subscription;
  routerCancelEventSubscription: Subscription;
  eventMessageSubscription: Subscription;
  loginState: LoginState;
  currentUserAuthInfo: UserInfo;
  solutionInfo: SolutionInfo;
  globalMenu: GlobalMenu[];
  activeTabIndex: number;
  prevActiveURL: string;
  TabColor = TabColor;
  tabs: Tab[] = [];
  tabPaddingLeft: string = "16px";
  currentSolEnvName:string = "";
  isInit:boolean = false;

  constructor(private authService: AuthenticationService,
    private eventService: EventService,
    private solutionService: SolutionService,
    private router: Router,
    private wsService: WebsocketService,
    private generalService: GeneralService) { }

  ngOnInit(): void {
    this.wsService.connect();
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.globalMenu = Utils.getGlobalMenu(this.currentUserAuthInfo);
    this.globalMenu.forEach((menu:GlobalMenu) => {
      this.tabs.push(new Tab(menu.id, menu.label, undefined, false));
    });
    //get solution info between routers
    this.routerEventSubscription = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      if (e.url != "/login") {
        if(this.authService.isLoggedIn()){
          this.currentUserAuthInfo = this.authService.getLoggedInUser();
          this.getSolutionInfo();
        } else {
          this.router.navigate(['login']);
        }
      }

      if(!this.isInit || this.prevActiveURL != e.url){
        this.setActiveTabIndex(e.url);
        this.isInit = true;
      }
      this.prevActiveURL = e.url;
    });
    this.routerCancelEventSubscription = this.router.events.pipe(filter(e => e instanceof NavigationCancel)).subscribe((e: NavigationCancel) => {
      this.setActiveTabIndex(this.prevActiveURL);
    });
    if (this.isForceGetSolutionInfo) {
      this.getSolutionInfo();
      this.setActiveTabIndex(this.router.url);
    }
    this.subscribeToEvents();
    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      this.processEventMessage(msg);
    });

  }

  ngOnDestroy() {
    this.loginEventSubscription.unsubscribe();
    this.routerEventSubscription.unsubscribe();
    this.routerCancelEventSubscription.unsubscribe();
    this.eventMessageSubscription.unsubscribe();
    this.wsService.disconnect();
  }

  onSelectedTabChange(selectedIndex: number):void {
    if(selectedIndex != this.activeTabIndex){
      this.activeTabIndex = selectedIndex;
      this.router.navigateByUrl(this.globalMenu[this.activeTabIndex].path);
      this.isInit = true;
    }else{
      if(this.isInit && this.router.url != this.globalMenu[this.activeTabIndex].path){
        this.router.navigateByUrl(this.globalMenu[this.activeTabIndex].path);
      }
    }
  }
  
  private getSolutionInfo(isSignIn: boolean = false, manifestUpdate: boolean = false): void {
    if (this.currentUserAuthInfo) {
      this.solutionService.getSolutionInfo(this.currentUserAuthInfo.settings.environmentId, this.currentUserAuthInfo.settings.solutionName).pipe(
        tap(resp => {
          this.loginState = LoginState.SignedIn;
          this.solutionInfo = resp.body;
          if (this.solutionInfo.currentEnvironment) {
            this.currentSolEnvName = `${this.solutionInfo.currentEnvironment.solName} : ${this.solutionInfo.currentEnvironment.name}`;
            this.eventService.broadcastSolutionInfo(this.solutionInfo);
            //the current environment was removed
          } else if (this.solutionInfo.environments.length > 0) {
            const nextAvailEnv = this.solutionInfo.environments[0];
            //switch to the next available environment
            this.solutionService.switchEnvironment(nextAvailEnv.id).pipe(tap(resp => {
              if (resp) {
                //update environment
                this.currentUserAuthInfo.settings.environmentId = nextAvailEnv.id;
                this.currentUserAuthInfo.settings.solutionName = nextAvailEnv.solName;
                this.authService.updateLoggedInUser(this.currentUserAuthInfo);
                //show warning
                let msg = new MsgPayLoad(undefined, {
                  message: $localize`You do not have access to the current environment. Your current environment has been switched to '${nextAvailEnv.solName} : ${nextAvailEnv.name}'`,
                });
                msg.setEvent(Events.WarningMsg);
                Utils.addHeaderMessageBanner(this.eventService, msg);
                this.getSolutionInfo();
              }
            })
            ).subscribe();
            //no access to any environments
          } else {
            this.currentUserAuthInfo = new UserInfo();
            this.authService.logout();
          }
        })
      ).subscribe();
    }
  }

  private subscribeToEvents(): void {
    this.loginEventSubscription = this.eventService.loginState$.subscribe(newLoginState => {
      //user loggedin
      if (newLoginState === LoginState.SignedIn) {
        this.currentUserAuthInfo = this.authService.getLoggedInUser();
        this.getSolutionInfo(true);
        //user loggedout
      } else if (newLoginState === LoginState.SignedOut || newLoginState === LoginState.SessionTimedOut) {
        //ignore
      } else {
        this.currentUserAuthInfo = new UserInfo();
        this.authService.logout(newLoginState);
      }
    });
  }

  private processEventMessage(msg: MsgPayLoad): void {
    switch (msg.eventType) {
      case Events.NotificationRefresh:
        //check if enviroment Id is set
        if(msg.headers.envId !== undefined && msg.headers.envId > 0){
          //check if the notification refresh message is for the current environment
          if(this.solutionInfo && this.solutionInfo.currentEnvironment && 
            this.solutionInfo.currentEnvironment.id == msg.headers.envId){
            this.getSolutionInfo();
          }
        }else{
          this.getSolutionInfo();
        }
        break;
      case Events.DiscoverExt:
      case Events.Discover:
        if (msg.body != undefined) {
          let discoverMsgData: any = JSON.parse(msg.body.msg);
          let name: string = discoverMsgData.systemName;
          let productsDiscovered: any = JSON.parse(discoverMsgData.alertData);
          let newProduct: number = 0;
          let removedProduct: number = 0;
          let unchangedProduct: number = 0;

          if (productsDiscovered.length > 0) {
            productsDiscovered.forEach(product => {
              if (product.flag == 'added') {
                newProduct++;
              } else if (product.flag == "removed") {
                removedProduct++;
              } else {
                unchangedProduct++;
              }
            });

            if (newProduct > 0 && removedProduct > 0) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Some products have been added and uninstalled from this system.`,
                new MessageBannerLink("discover-product-details", $localize`Show Details`, {name: name, products: productsDiscovered}));
            } else if (newProduct > 0) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`New products have been discovered.`,
                new MessageBannerLink("discover-product-details", $localize`Show Details`, {name: name, products: productsDiscovered}));
            } else if (removedProduct > 0) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Some products have been uninstalled from this system.`,
                new MessageBannerLink("discover-product-details", $localize`Show Details`, {name: name, products: productsDiscovered}));
            } else {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.InfoMsg, $localize`There are currently no new products on this system.`);
            }

          } else {
            Utils.addHeaderMessageEventBanner(this.eventService, Events.InfoMsg, $localize`No products were discovered on this system.`);
          }
        }
        break;
      case Events.EnableProduct:
        if (msg.body == undefined) {
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Unable to enable the product. Please try again.`);
        } else {
          if (msg.body.isEnabled) {
            Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`A component instance has been enabled.`,
              new MessageBannerLink("show-enable-details", $localize`Show Details`, msg.body));
          } else if(msg.body.errorCode == 'UPDATE_MISMATCH_ERROR'){
            Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`The specified product update level did not match the product analysis results. Please check the logs for more details.`);
          } else{
            Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`There was an error trying to enable a component instance.`);  
          }
        }
        break;
      case Events.RefreshSolution:
        this.getSolutionInfo();
        break;
      case Events.AuthMethodChanged:
        if(Utils.isSuperuserRole(this.currentUserAuthInfo)){
          this.currentUserAuthInfo.settings.authenticationMethod = msg.body.authenticationMethod;
          this.authService.updateLoggedInUser(this.currentUserAuthInfo);
          Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`The authentication settings have been saved successfully.`);
        }else{
          this.eventService.broadcastLoginStateChange(LoginState.SignedOut);
        }
        break;
      case Events.ManifestUpdateComplete:
        this.updateManifestStatus();
        break;
      case Events.PackageLogs:
        if (msg.body.success){
          Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`A logs package has been created and is ready for download.`);
        }else {
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`A logs package has failed to be created.`);
        }
        break;
      case Events.PackageProdLogs:
        if (msg.body.success){
          Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`A Product Logs Package has been created and is ready for download.`);
        }else {
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`A Product Logs Package has failed to be created.`);
        }
        break;
      case Events.ActivityTaskQueueFailure:
        if(msg.body.activityType){
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`A '${Utils.getActivityTaskName(msg.body.activityType)}' task has failed to be queued, please check the logs and try again.`);
        } else {
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`A task has failed to be queued, please check the logs and try again.`);
        }

        break;
    }
  }

  updateManifestStatus(): void{
    this.generalService.getManifestSettings().pipe(tap(resp => {
      this.solutionInfo.manifestStatus = resp.body;

      if (Utils.isSystemUpdateRequired(this.solutionInfo)) {
        Utils.postSystemUpdateMessage(this.solutionInfo, this.eventService);
      }

      Utils.processSystemNotifications(this.solutionInfo, this.currentUserAuthInfo);

      if(this.solutionInfo.manifestStatus.mandatoryUpdateRequired){
        Utils.postManditoryManifestUpdateMessage(this.eventService);
      }
    })).subscribe();
  }

  private setActiveTabIndex(url:string): void {
    if(url && url.length > 0){
      for(var i:number = 0; i < this.globalMenu.length; i++){
        if(url.startsWith(this.globalMenu[i].path)){
          this.activeTabIndex = i;
          break;
        }
      }
    }
  }
}
